/* ==========================================================================
   SORT DROPDOWN - frame 618:2

   The native <select> stays in the DOM and stays the source of truth. This
   control writes to it and dispatches `change`, so ob-collection.js and every
   other existing listener keep working untouched.

   The panel ALWAYS opens downward. Where there is not enough room below the
   trigger it gets a max-height and scrolls internally. It never flips up -
   browser-chosen direction is the defect this control exists to remove.
   ========================================================================= */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.obSortdInit === '1') return;
    var select  = root.querySelector('[data-ob-sort]');
    var trigger = root.querySelector('[data-ob-sortd-trigger]');
    var panel   = root.querySelector('[data-ob-sortd-panel]');
    var current = root.querySelector('[data-ob-sortd-current]');
    if (!select || !trigger || !panel) return;
    root.dataset.obSortdInit = '1';

    var opts = [].slice.call(panel.querySelectorAll('[data-ob-sortd-opt]'));
    if (!opts.length) return;

    var SHORT = {
      '': 'Featured', 'manual': 'Featured', 'relevance': 'Relevant',
      'title-ascending': 'A-Z', 'title-descending': 'Z-A',
      'price-ascending': 'Price low', 'price-descending': 'Price high',
      'created-ascending': 'Oldest', 'created-descending': 'Newest'
    };

    var activeIndex = Math.max(0, opts.findIndex(function (o) {
      return o.getAttribute('aria-selected') === 'true';
    }));

    /* The panel is anchored under the trigger and must stay there. Rather than
       flipping, measure the real distance to the bottom of the viewport and
       cap the height, leaving a small margin so it never sits flush against
       the edge. 176px floor = four rows, below which scrolling is pointless
       and the panel would be unusable. */
    function sizePanel() {
      var r = trigger.getBoundingClientRect();
      var room = window.innerHeight - r.bottom - 12;
      var natural = opts.length * 44 + 3;
      panel.style.setProperty('--ob-sortd-max', Math.max(176, Math.min(natural, room)) + 'px');
    }

    function setActive(i, scroll) {
      activeIndex = (i + opts.length) % opts.length;
      opts.forEach(function (o, n) { o.classList.toggle('is-active', n === activeIndex); });
      var el = opts[activeIndex];
      panel.setAttribute('aria-activedescendant', el.id || '');
      if (scroll && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    var hideTimer = null;
    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function open() {
      if (root.classList.contains('is-open')) return;
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      sizePanel();
      panel.hidden = false;
      // The panel is un-hidden and focusable RIGHT NOW - the animation never
      // gates interactivity. `.is-open` is added on the next frame purely so
      // the transition has a painted starting state to move from; without the
      // frame the browser coalesces both and no animation runs at all.
      requestAnimationFrame(function () { root.classList.add('is-open'); });
      trigger.setAttribute('aria-expanded', 'true');
      setActive(opts.findIndex(function (o) {
        return o.getAttribute('aria-selected') === 'true';
      }), true);
      panel.focus();
      document.addEventListener('click', onDocClick, true);
      window.addEventListener('resize', sizePanel);
      window.addEventListener('scroll', sizePanel, { passive: true });
    }

    function close(focusTrigger) {
      if (!root.classList.contains('is-open')) return;
      root.classList.remove('is-open');
      // Hide AFTER the 110ms exit so it animates out rather than vanishing.
      // Reopening clears this, so a fast toggle cannot hide a panel that has
      // just been reopened.
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(function () {
        panel.hidden = true; hideTimer = null;
      }, reduceMotion ? 0 : 120);
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onDocClick, true);
      window.removeEventListener('resize', sizePanel);
      window.removeEventListener('scroll', sizePanel);
      if (focusTrigger) trigger.focus();
    }

    function onDocClick(e) { if (!root.contains(e.target)) close(false); }

    /* Write to the native select and dispatch change. This is the only place
       the sort actually changes - the custom control never navigates itself. */
    function choose(el) {
      var value = el.getAttribute('data-value');
      opts.forEach(function (o) {
        var on = o === el;
        o.classList.toggle('is-selected', on);
        o.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (current) current.textContent = SHORT[value] || (el.textContent || '').trim();
      close(true);
      if (select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    trigger.addEventListener('click', function () {
      root.classList.contains('is-open') ? close(false) : open();
    });

    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault(); open();
      } else if (e.key === 'ArrowDown') { e.preventDefault(); open(); }
    });

    panel.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); setActive(activeIndex + 1, true); break;
        case 'ArrowUp':   e.preventDefault(); setActive(activeIndex - 1, true); break;
        case 'Home':      e.preventDefault(); setActive(0, true); break;
        case 'End':       e.preventDefault(); setActive(opts.length - 1, true); break;
        case 'Enter':
        case ' ':
        case 'Spacebar':  e.preventDefault(); choose(opts[activeIndex]); break;
        case 'Escape':    e.preventDefault(); close(true); break;
        case 'Tab':       close(false); break;
      }
    });

    opts.forEach(function (o, i) {
      o.addEventListener('click', function () { choose(o); });
      o.addEventListener('mousemove', function () { setActive(i, false); });
    });

    /* Keep the trigger honest if anything else changes the select - the Shop
       All default is applied by ob-collection.js after load, for instance. */
    select.addEventListener('change', function () {
      var v = select.value;
      opts.forEach(function (o) {
        var on = o.getAttribute('data-value') === v;
        o.classList.toggle('is-selected', on);
        o.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (current) current.textContent = SHORT[v] || v;
    });
  }

  function boot() {
    document.querySelectorAll('[data-ob-sortd]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // The collection grid re-renders through the Section Rendering API, which
  // replaces the toolbar wholesale. Re-init after any such swap.
  document.addEventListener('ob:collection:rendered', boot);
})();
